import type { AuthConfig } from './config';

export class AuthError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/** Read all cookies as a raw string. Wrapper to satisfy linter rules. */
function readCookies(): string {
  return document.cookie;
}

/** Read the cf-expires cookie value (epoch seconds). */
export function getCfExpires(): number | null {
  const match = readCookies().match(/(?:^|;\s*)cf-expires=(\d+)/);
  return match ? Number(match[1]) : null;
}

/** Call cookie-issuer to set CloudFront signed cookies. */
export async function issueCookie(
  idToken: string
): Promise<{ ok: boolean; expiresAt: number }> {
  const res = await fetch('/auth/issue-cookie', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new AuthError(
      `Cookie issuance failed (${res.status}): ${text}`,
      res.status
    );
  }

  // 外部レスポンスなので形を検証してから返す（この package は zod 非依存のため
  // in / typeof で narrow する）。
  const data: unknown = await res.json();
  if (
    typeof data === 'object' &&
    data !== null &&
    'ok' in data &&
    typeof data.ok === 'boolean' &&
    'expiresAt' in data &&
    typeof data.expiresAt === 'number'
  ) {
    return { ok: data.ok, expiresAt: data.expiresAt };
  }
  throw new AuthError('Malformed cookie issuance response', res.status);
}

/**
 * Build the Cognito /oauth2/authorize URL for retrying with a different account.
 * Forces account selection (prompt=select_account) so the user can switch accounts
 * after a domain-not-allowed rejection.
 */
export function buildAuthorizeUrlForRetry(
  config: AuthConfig,
  returnTo: string
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: config.redirectSignIn,
    identity_provider: 'Google',
    scope: 'email openid profile',
    state: btoa(returnTo),
    prompt: 'select_account',
  });
  return `https://${config.cognitoDomain}/oauth2/authorize?${params}`;
}
